import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Activity, Target, Server, BarChart3, Users, Cog, GraduationCap,
  ArrowRight, CheckCircle, Sparkles, TrendingUp, Download
} from 'lucide-react';

const DigitalMaturitySection = () => {
  const dimensions = [
    { icon: Target, name: 'Strategy & Governance', description: 'Digital strategy alignment and governance' },
    { icon: Server, name: 'Technology & Infrastructure', description: 'Cloud adoption and architecture maturity' },
    { icon: BarChart3, name: 'Data & Analytics', description: 'Data-driven decision making capabilities' },
    { icon: Users, name: 'Customer Experience', description: 'Omnichannel and personalization' },
    { icon: Cog, name: 'Operations & Processes', description: 'Process automation and efficiency' },
    { icon: GraduationCap, name: 'Workforce & Culture', description: 'Digital skills and innovation culture' },
  ];

  const features = [
    'Executive-level AI analysis',
    'Industry benchmarks',
    'Strategic roadmap',
    'Quick wins identification',
    'Investment priorities',
    'PPTX report download'
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-gray-900 via-gray-900 to-gray-800 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-cyan-500/5 to-emerald-500/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 rounded-full px-4 py-1.5 mb-6">
            <Sparkles className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium text-cyan-300">New AI-Powered Assessment</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Digital Maturity Assessment
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Discover your organization&apos;s digital transformation readiness with our comprehensive 
            AI-powered assessment, benchmarked against industry leaders.
          </p>
        </div>

        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Dimensions */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Activity className="w-5 h-5 text-cyan-400" />
              6 Assessment Dimensions
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {dimensions.map((dim, index) => {
                const Icon = dim.icon;
                return (
                  <div 
                    key={index}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/10 hover:border-cyan-500/30 transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:from-cyan-500/30 group-hover:to-emerald-500/30 transition-colors">
                        <Icon className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white text-sm">{dim.name}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">{dim.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right side - Features & CTA */}
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              What You&apos;ll Get
            </h3>
            
            <div className="grid grid-cols-2 gap-3 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-sm text-gray-300">{feature}</span>
                </div>
              ))}
            </div>

            {/* Maturity levels preview */}
            <div className="bg-black/20 rounded-xl p-4 mb-8">
              <p className="text-xs text-gray-500 mb-3 uppercase tracking-wide">Maturity Levels</p>
              <div className="flex items-center gap-1">
                <div className="flex-1 h-2 bg-red-500/60 rounded-l-full" title="Digital Novice" />
                <div className="flex-1 h-2 bg-orange-500/60" title="Digital Explorer" />
                <div className="flex-1 h-2 bg-yellow-500/60" title="Digital Performer" />
                <div className="flex-1 h-2 bg-green-500/60" title="Digital Leader" />
                <div className="flex-1 h-2 bg-emerald-600/80 rounded-r-full" title="Digital Champion" />
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-gray-500">
                <span>Novice</span>
                <span>Explorer</span>
                <span>Performer</span>
                <span>Leader</span>
                <span>Champion</span>
              </div>
            </div>

            {/* CTA */}
            <Link 
              to="/digital-maturity"
              className="group flex items-center justify-center gap-2 w-full bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white font-semibold py-4 px-6 rounded-xl transition-all shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/30"
            >
              Start Assessment
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <p className="text-center text-xs text-gray-500 mt-3">
              Takes ~10 minutes • No login required • Instant results
            </p>
          </div>
        </div>

        {/* Bottom stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '6', label: 'Assessment Dimensions' },
            { value: '19', label: 'Strategic Questions' },
            { value: '5', label: 'Maturity Levels' },
            { value: '10+', label: 'Report Slides' }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                {stat.value}
              </div>
              <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DigitalMaturitySection;
